import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { call } from "@/lib/contracts";
import {
  loginContract, passkeyAuthOptionsContract, passkeyAuthVerifyContract,
  passkeyRegisterOptionsContract, passkeyRegisterVerifyContract,
  credentialsContract, logoutContract, type AuthUser,
} from "@/lib/contracts/auth";
import { tokenStore, ApiError } from "@/lib/http";
import { queryClient } from "@/lib/queryClient";

/*
 * Auth, with passkey enrolment MANDATORY.
 *
 * Product decision: a user who signs in on a device with no passkey registered
 * cannot reach the dashboard until they enrol one. There is no skip.
 *
 * The consequence to be aware of, since it is the direct cost of that choice:
 * a passkey is bound to the device (or to the platform keychain syncing it).
 * A user who signs in on a borrowed laptop must enrol a passkey there, and a
 * user whose only device is lost recovers only through the password/Google
 * path — which is why those two remain available as the bootstrap, and why
 * `enrolError` is surfaced rather than swallowed.
 *
 * Order is fixed by the API: POST /passkey/register/options requires an
 * authenticated session, so enrolment can only ever follow a first sign-in by
 * password or Google. That matches the "force after first sign-in" rule
 * exactly.
 */

export type AuthStatus =
  | "checking"     /* verifying an existing token                          */
  | "signed-out"   /* no session — show the unlock screen                  */
  | "enrolling"    /* signed in, zero passkeys — BLOCKED until one exists  */
  | "unavailable"  /* signed in, but the API could not answer — retryable  */
  | "ready";       /* passkey present, dashboard reachable                 */

interface AuthValue {
  status: AuthStatus;
  user: AuthUser | null;
  /** True only after a passkey sign-in: the vault key is unwrapped. */
  vaultUnlocked: boolean;
  busy: boolean;
  error: string | null;
  enrolError: string | null;
  signInWithPasskey: () => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  enrolPasskey: () => Promise<void>;
  /** Re-evaluate the passkey gate (after an API outage). */
  retryGate: () => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

function message(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.kind === "network") return "Can't reach the API.";
    return err.message;
  }
  /* WebAuthn rejects with a DOMException the user caused (cancelled the
   * prompt, no matching credential). Those are not failures worth alarming
   * language. */
  if (err instanceof DOMException) {
    if (err.name === "NotAllowedError") return "Cancelled, or the device timed out.";
    if (err.name === "InvalidStateError") return "This device already has a passkey for this account.";
    if (err.name === "NotSupportedError") return "This browser or device can't do passkeys.";
    return err.message || fallback;
  }
  return err instanceof Error ? err.message : fallback;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("checking");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [vaultUnlocked, setVaultUnlocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrolError, setEnrolError] = useState<string | null>(null);
  const mounted = useRef(true);

  /* Set on the way IN as well as cleared on the way out.
   *
   * StrictMode mounts, unmounts and remounts every component in development.
   * With only the cleanup, that first synthetic unmount latched this to false
   * permanently — so every setState after an await short-circuited and the gate
   * sat in "checking" forever, rendering an empty page. It reproduced only when
   * a token was present, because the signed-out path sets state synchronously
   * before any await and never consults this flag. */
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  /* Decide between "enrolling" and "ready" by asking the server how many
   * passkeys this account has. Never trusted to local state: the count can
   * change on another device. */
  const resolveGate = useCallback(async () => {
    try {
      const { count } = await call(credentialsContract, undefined);
      if (!mounted.current) return;
      setStatus(count > 0 ? "ready" : "enrolling");
    } catch (err) {
      if (!mounted.current) return;
      if (err instanceof ApiError && err.kind === "unauthorized") {
        setStatus("signed-out");
        return;
      }
      /* The gate cannot be evaluated — the API is down or the endpoint is
       * broken. Failing OPEN would silently defeat the mandate, so it still
       * fails CLOSED — but into a dedicated "unavailable" state, not into
       * enrolment. Showing "Register this device" to someone whose device IS
       * registered, because a 503 hid the answer, reads as the product
       * forgetting them. The honest statement is "we can't check right now". */
      setEnrolError(message(err, "Couldn't check this device's passkeys."));
      setStatus("unavailable");
    }
  }, []);

  /* Session bootstrap. */
  useEffect(() => {
    if (!tokenStore.get()) { setStatus("signed-out"); return; }
    void resolveGate();
  }, [resolveGate]);

  /* http.ts broadcasts when a refresh fails, so a session that expires mid-use
   * drops straight back to the unlock screen instead of erroring per-request. */
  useEffect(() => {
    const onOut = () => {
      setUser(null);
      setVaultUnlocked(false);
      setStatus("signed-out");
      queryClient.clear();
    };
    window.addEventListener("kontact:signed-out", onOut);
    return () => window.removeEventListener("kontact:signed-out", onOut);
  }, []);

  const signInWithPasskey = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      /* No email: the discoverable-credential flow lets the browser offer
         whichever passkey it holds, so nothing is typed. */
      const options = await call(passkeyAuthOptionsContract, {}, { anonymous: true });
      const response = await startAuthentication({ optionsJSON: options as never });
      const result = await call(passkeyAuthVerifyContract, { response }, { anonymous: true });

      tokenStore.set(result.token);
      setUser(result.user);
      setVaultUnlocked(result.vaultUnlocked);
      /* Signing in WITH a passkey proves one exists — the gate is satisfied
         without another round trip. */
      setStatus("ready");
    } catch (err) {
      setError(message(err, "Couldn't unlock with a passkey."));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    setBusy(true); setError(null);
    try {
      const result = await call(loginContract, { email, password }, { anonymous: true });
      tokenStore.set(result.token, result.refreshToken);
      setUser(result.user);
      setVaultUnlocked(false);
      /* Password sign-in says nothing about passkeys — ask. */
      await resolveGate();
    } catch (err) {
      setError(message(err, "Couldn't sign in."));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [resolveGate]);

  const enrolPasskey = useCallback(async () => {
    setBusy(true); setEnrolError(null);
    try {
      const options = await call(passkeyRegisterOptionsContract, undefined);
      const response = await startRegistration({ optionsJSON: options as never });
      const result = await call(passkeyRegisterVerifyContract, {
        response,
        deviceName: navigator.userAgent.slice(0, 80),
      });
      if (!result.verified) throw new Error("The device's response was rejected.");
      setStatus("ready");
    } catch (err) {
      setEnrolError(message(err, "Couldn't register a passkey on this device."));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, []);

  const retryGate = useCallback(async () => {
    setBusy(true); setEnrolError(null);
    try { await resolveGate(); } finally { if (mounted.current) setBusy(false); }
  }, [resolveGate]);

  const signOut = useCallback(() => {
    void call(logoutContract, undefined).catch(() => {});
    tokenStore.clear();
    queryClient.clear();
    setUser(null);
    setVaultUnlocked(false);
    setStatus("signed-out");
  }, []);

  const value = useMemo<AuthValue>(() => ({
    status, user, vaultUnlocked, busy, error, enrolError,
    signInWithPasskey, signInWithPassword, enrolPasskey, retryGate, signOut,
  }), [status, user, vaultUnlocked, busy, error, enrolError,
       signInWithPasskey, signInWithPassword, enrolPasskey, retryGate, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
